import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Popconfirm, Select, Space, Typography } from 'antd';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../const/routes';
import { useActiveSchoolId } from '../../hooks/useActiveSchoolId';
import { trpc } from '../../utils/trcp';
import TagDisplay from './TagDisplay';

const { Text } = Typography;

interface EntityTagManagerProps {
	entityId: string;
	entityType: 'STUDENT' | 'AUTO'; // Můžeme rozšířit o další typy
	onTagsChange?: (tags: any[]) => void;
}

const EntityTagManager: React.FC<EntityTagManagerProps> = ({ entityId, entityType, onTagsChange }) => {
	const { activeSchoolId } = useActiveSchoolId();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedTagId, setSelectedTagId] = useState<string | undefined>();

	// Queries
	const { data: availableTags } = trpc.tag.getTagsBySchoolId.useQuery({
		schoolId: activeSchoolId,
	});

	const { data: entityTags, refetch: refetchEntityTags } = trpc.tag.getEntityTags.useQuery({
		entityId,
		entityType,
		schoolId: activeSchoolId,
	});

	// Mutations
	const addTagMutation = trpc.tag.addTagToEntity.useMutation({
		onSuccess: () => {
			refetchEntityTags();
			onTagsChange?.(entityTags || []);
		},
	});

	const removeTagMutation = trpc.tag.removeTagFromEntity.useMutation({
		onSuccess: () => {
			refetchEntityTags();
			onTagsChange?.(entityTags || []);
		},
	});

	const handleAddTag = async () => {
		if (!selectedTagId) {
			return;
		}

		await addTagMutation.mutateAsync({
			entityId,
			entityType,
			tagId: selectedTagId,
			schoolId: activeSchoolId,
		});

		setSelectedTagId(undefined);
		setIsModalVisible(false);
	};

	const handleRemoveTag = async (tagId: string) => {
		await removeTagMutation.mutateAsync({
			entityId,
			entityType,
			tagId,
			schoolId: activeSchoolId,
		});
	};

	// Filtruj dostupné štítky (ty, které ještě nejsou přiřazené)
	const assignedTagIds = entityTags?.map(tag => tag.id) || [];
	const availableTagsForSelection = availableTags?.filter(tag => !assignedTagIds.includes(tag.id)) || [];

	return (
		<div>
			<Space wrap style={{ marginBottom: 8 }}>
				{entityTags?.map(tag => (
					<Space.Compact key={tag.id}>
						<TagDisplay tag={tag} />
						<Popconfirm
							title="Odebrat štítek"
							description="Opravdu chcete odebrat tento štítek?"
							onConfirm={() => handleRemoveTag(tag.id)}
							okText="Ano"
							cancelText="Ne"
						>
							<Button
								size="small"
								type="text"
								icon={<CloseOutlined />}
								style={{ color: 'rgba(0, 0, 0, 0.45)' }}
							/>
						</Popconfirm>
					</Space.Compact>
				))}
			</Space>

			<div>
				<Button
					size="small"
					type="dashed"
					icon={<PlusOutlined />}
					onClick={() => setIsModalVisible(true)}
					disabled={availableTagsForSelection.length === 0}
				>
					Přidat štítek
				</Button>

				{availableTagsForSelection.length === 0 && availableTags && availableTags.length > 0 && (
					<Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
						Všechny dostupné štítky jsou již přiřazené
					</Text>
				)}

				{availableTags && availableTags.length === 0 && (
					<Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
						<Link to={`${ROUTES.APP.SETTINGS}/tags`}>Vytvořit první štítek</Link>
					</Text>
				)}
			</div>

			<Modal
				title="Přidat štítek"
				open={isModalVisible}
				onOk={handleAddTag}
				onCancel={() => {
					setIsModalVisible(false);
					setSelectedTagId(undefined);
				}}
				okText="Přidat"
				cancelText="Zrušit"
				okButtonProps={{ disabled: !selectedTagId, loading: addTagMutation.isLoading }}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					<div>
						<Text>Vyberte štítek k přidání:</Text>
					</div>

					<Select
						placeholder="Vyberte štítek"
						style={{ width: '100%' }}
						value={selectedTagId}
						onChange={setSelectedTagId}
						optionLabelProp="label"
					>
						{availableTagsForSelection.map(tag => (
							<Select.Option key={tag.id} value={tag.id} label={tag.name}>
								<TagDisplay tag={tag} />
							</Select.Option>
						))}
					</Select>

					<div style={{ textAlign: 'center', marginTop: 8 }}>
						<Link to={`${ROUTES.APP.SETTINGS}/tags`} target="_blank">
							Vytvořit nový štítek
						</Link>
					</div>
				</Space>
			</Modal>
		</div>
	);
};

export default EntityTagManager;
